import { Injectable } from '@angular/core';

import { Circle, Fill, Stroke, Style, Text } from 'ol/style';

@Injectable({
  providedIn: 'root',
})
export class MapStyleService {
  getInvisibleStyle(): Style {
    const fill = new Fill({
      color: `rgba(0,0,0,0)`,
    });
    const stroke = new Stroke({
      color: `rgba(0,0,0,0)`,
    });
    const style = new Style({
      fill,
      image: new Circle({
        fill,
        radius: 5,
        stroke,
      }),
      stroke,
    });
    return style;
  }

  getTextStyle(label: string, color?: string): Style {
    const fill = new Fill({
      color: color || 'black',
    });
    const stroke = new Stroke({
      color: 'white',
      width: 2,
    });
    const style = new Style({
      text: new Text({
        fill,
        font: '16px liberationserif_b, sans-serif',
        offsetY: -10,
        overflow: true,
        stroke,
        text: label,
      }),
    });
    return style;
  }

  getDrawStyle(): Style {
    const fill = new Fill({
      color: `rgba(255, 255, 255, .3)`,
    });
    const stroke = new Stroke({
      color: `rgba(0,0,0,1)`,
      lineDash: [10, 15],
      width: 2,
    });
    const circleStroke = new Stroke({
      color: `rgba(0,0,0,1)`,
      width: 2,
    });
    const style = new Style({
      fill,
      image: new Circle({
        fill,
        radius: 5,
        stroke: circleStroke,
      }),
      stroke,
      zIndex: Infinity,
    });
    return style;
  }

  /** Default select styles in openlayers, used when highlighting an item */
  getHighlightStyle(): Style[] {
    const white = [255, 255, 255, 1];
    const yellow = [255, 213, 0, 1];
    const width = 3;
    const styles = [
      new Style({
        fill: new Fill({
          color: [255, 255, 255, 0.5],
        }),
        image: new Circle({
          fill: new Fill({
            color: yellow,
          }),
          radius: width * 2,
          stroke: new Stroke({
            color: white,
            width: width / 2,
          }),
        }),
        stroke: new Stroke({
          color: white,
          width: width + 2,
        }),
        zIndex: Infinity,
      }),
      new Style({
        stroke: new Stroke({
          color: yellow,
          width,
        }),
        zIndex: Infinity,
      }),
    ];
    return styles;
  }

  // eslint-disable-next-line max-lines-per-function
  generateStyle(
    color: string,
    strokeWidth?: number,
    zIndex?: number,
    text?: { label: string; subLabel?: string },
  ): Style[] {
    const fill = new Fill({
      color: `rgba(255, 255, 255, .3)`,
    });
    const circleFill = new Fill({
      color,
    });
    const stroke = new Stroke({
      color: `rgba(255, 255, 255, .5)`,
      width: strokeWidth || 5,
    });
    const circleStroke = new Stroke({
      color: `rgba(255, 255, 255, .5)`,
      width: 2,
    });
    const style = [
      new Style({
        fill,
        image: new Circle({
          fill: circleFill,
          radius: strokeWidth || 5,
          stroke: circleStroke,
        }),
        stroke,
        text: text?.label
          ? new Text({
              fill: new Fill({
                color: 'black',
              }),
              font: '16px liberationserif_b, sans-serif',
              offsetY: -10,
              overflow: true,
              stroke: new Stroke({
                color: 'white',
                width: 2,
              }),
              text: text?.label,
            })
          : undefined,
        zIndex: zIndex ?? Infinity,
      }),
      new Style({
        text: new Text({
          fill: new Fill({
            color: 'white',
          }),
          font: '12px liberationserif_b, sans-serif',
          offsetY: 8,
          overflow: true,
          stroke: new Stroke({
            color: 'black',
            width: 2,
          }),
          text: text?.subLabel,
        }),
      }),
      new Style({
        stroke: new Stroke({
          color,
          width: strokeWidth || 3,
        }),
        zIndex: zIndex ?? Infinity,
      }),
    ];

    return style;
  }

  /** create a style with random colours */
  generateRandomStyle(id?: number): Style {
    const generatedColor = this.generateColours(id);
    const fill = new Fill({
      color: generatedColor.background,
    });
    const stroke = new Stroke({
      color: generatedColor.border,
      width: 2.5,
    });
    const style = new Style({
      fill,
      image: new Circle({
        fill,
        radius: 5,
        stroke,
      }),
      stroke,
    });
    return style;
  }

  removeTextFromStyle(style: Style[]): Style[] {
    const newStyle = style.map((s) => {
      const clonedStyle = s.clone();
      if (clonedStyle.getText()) {
        clonedStyle.getText().setText('');
      }
      return clonedStyle;
    });
    return newStyle;
  }

  private generateColours(id?: number): { background: string; border: string } {
    const array = [];
    for (let i = 0; i < 3; ++i) {
      array.push(Math.round(Math.random() * 255));
    }

    let color;
    if (id) {
      color = this.hexToRgb(this.idToColor(id + ''));
    }

    const colorArray = color || array;

    const backgroundColor = `rgba(${colorArray[0] + 20},${colorArray[1] + 20},${
      colorArray[2] + 20
    },0.3)`;
    const borderColor = `rgba(${colorArray[0] - 20},${colorArray[1] - 20},${
      colorArray[2] - 20
    },1)`;

    return { background: backgroundColor, border: borderColor };
  }

  private idToColor(id: string) {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + (Number(hash < 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = Math.abs(Math.sin(hash + i)) * 0xffff;
      color += Math.floor(value).toString(16);
    }
    return color;
  }
  private hexToRgb(hex: string): number[] {
    return hex
      .replace(/^#/, '')
      .match(/.{2}/g)
      .map((x) => parseInt(x, 16));
  }
}
